import { SxProps, Theme } from '@mui/material';
import { resolveTypographyVariantStyles } from '@aspendental/themes';

import { Sizes } from '@/types';
import { Maybe } from '@/types/generated';
import { IImageAsset } from '@/components/ImageAsset/ImageAsset';

export const wrapper = {
	display: 'block',
	margin: '0 auto',
	'& [data-testid="group_multicards_cards"]': {
		maxWidth: 'unset',
	},
};

export const backgroundImageParsed = (
	{ desktopImage, mobileImage }: IImageAsset,
	fixBackgroundToBottom?: Maybe<boolean>,
	isSmallScreen?: Maybe<boolean>
): object => {
	if (!desktopImage && !mobileImage) return {};

	const backgroundPosition = fixBackgroundToBottom ? 'bottom center' : 'center';
	const backgroundSize = fixBackgroundToBottom ? 'auto' : 'cover';
	const displayImage = isSmallScreen && mobileImage?.image ? mobileImage : desktopImage;
	const imageUrl = displayImage?.image?.cdn_url || '';

	return {
		backgroundPosition,
		backgroundSize,
		backgroundImage: `url(${imageUrl})`,
		backgroundRepeat: 'no-repeat',
	};
};

export const backgroundContainerStyles = (
	backgroundImage?: Maybe<IImageAsset>,
	fixBackgroundToBottom?: Maybe<boolean>,
	isSmallScreen?: Maybe<boolean>
): object => {
	return {
		paddingTop: isSmallScreen ? '2.5rem' : '5.125rem',
		paddingLeft: isSmallScreen ? '1.25rem' : '4.5rem',
		paddingRight: isSmallScreen ? '1.25rem' : '4.5rem',
		paddingBottom: isSmallScreen ? '2.5rem' : '7.1rem',
		...(backgroundImage && backgroundImageParsed(backgroundImage, fixBackgroundToBottom, isSmallScreen)),
	};
};

export const getHeaderSpacingValue = (headerSpacing: Maybe<Sizes> = 'Medium') => {
	const headerSpacingValues = {
		Small: 1.5,
		Medium: 2.5,
		Large: 4,
		XLarge: 6,
	} as { [key: string]: number };

	if (!headerSpacing) {
		return headerSpacingValues['Medium'];
	}

	return headerSpacingValues[headerSpacing];
};

export const getSectionWrapperStyles = (isSmallScreen: boolean, sectionAlignment?: Maybe<string>): SxProps => {
	return {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: sectionAlignment ?? 'flex-start',
		alignItems: isSmallScreen ? 'flex-start' : 'center',
		textAlign: sectionAlignment === 'center' ? 'center' : 'left',
		marginBottom: isSmallScreen ? '2.5rem' : '3.75rem',
		gap: '0.75rem',
		marginLeft: sectionAlignment === 'flex-start' ? 0 : 'auto',
		paddingX: '0px',
		'@media (min-width: 600px)': {
			paddingX: '0px',
		},
	};
};

export const getEyebrowStyles = (theme: Theme) => ({
	lineHeight: '1rem',
	fontFamily: 'Messina Sans',
	fontSize: '0.875rem',
	fontWeight: 700,
	letterSpacing: '0.188rem',
	marginBottom: '1rem',
	[theme.breakpoints.down('sm')]: {
		lineHeight: '1rem',
		fontSize: '0.875rem',
		fontWeight: 700,
		fontFamily: 'Messina Sans',
		letterSpacing: '0.188rem',
		marginBottom: '1rem',
	},
});

export const getCardsWrapperStyles = ({
	numberOfCards,
	isSmallScreen,
}: {
	isSmallScreen: boolean;
	numberOfCards: number;
}) => ({
	display: 'grid',
	gridTemplateColumns: isSmallScreen ? 'repeat(1, 1fr)' : `repeat(${numberOfCards}, 1fr)`,
	gap: isSmallScreen ? '2.5rem' : '1.5rem',
	alignItems: 'start',
	justifyItems: isSmallScreen ? 'left' : 'center',
	textAlign: 'center',
	padding: 0,
	boxSizing: 'border-box',
});

export const getHeadingStyles = (theme: Theme) => ({
	...resolveTypographyVariantStyles(theme, 'header1'),
	textAlign: 'center' as const,
	fontFamily: 'Messina Modern',
	lineHeight: '3.75rem',
	fontSize: '3.75rem',
	fontWeight: 300,
	[theme.breakpoints.down('sm')]: {
		textAlign: 'left' as const,
		fontSize: '2.625rem',
		lineHeight: '3rem',
	},
});

export const richTextDescStyles = (isSmallScreen: boolean) => ({
	'& h2': {
		color: 'white',
		fontSize: '1.5rem',
		fontWeight: 700,
		textAlign: isSmallScreen ? 'left' : 'center',
		lineHeight: '2rem',
		fontFamily: 'Messina Sans',
	},
	'& p': {
		color: 'white',
		fontFamily: 'Messina Modern',
		fontSize: '1.125rem',
	},
});

export const richTextDescStyles2 = (isSmallScreen: boolean) => ({
	'& p': {
		fontSize: isSmallScreen ? '1.5rem' : '1.875rem',
		lineHeight: isSmallScreen ? '2rem' : '2.5rem',
	},
});

export const richTextDescStyles3 = (isSmallScreen: boolean) => ({
	justifyItems: isSmallScreen ? null : 'center',
	'& h2': {
		color: 'white',
		fontSize: isSmallScreen ? '44px' : '2.292vw',
		fontWeight: 300,
		textAlign: isSmallScreen ? 'left' : 'center',
		lineHeight: '44px',
		fontFamily: 'Messina Modern',
		height: isSmallScreen ? 'auto' : '100px',
		alignContent: 'center',
		width: isSmallScreen ? '100%' : '80%',
		margin: 'auto',
	},
	'& p': {
		color: '#B9DEFA',
		fontFamily: 'Messina Sans',
		fontSize: '16px',
	},
});

export const getMultiCardButtonsStyles = (isSmallScreen: boolean) => ({
	display: 'flex',
	alignContent: 'center',
	justifyContent: 'center',
	gap: '1rem',
	mt: isSmallScreen ? 0 : '3.75rem',
	flexWrap: 'wrap',
});

export const multiCardChipStyles = {
	color: 'secondary.main',
	borderColor: 'secondary.main',
	'&:hover': {
		color: 'secondary.main',
		backgroundColor: 'neutrals.white',
		opacity: 0.8,
	},
	'&.Mui-selected': {
		backgroundColor: 'secondary.main',
		color: 'neutrals.white',
		'&:hover': {
			color: 'neutrals.white',
			backgroundColor: 'secondary.main',
			opacity: 0.8,
		},
	},
};
